import {action, observable} from 'mobx';
import {EventEmitter} from 'events';
import {
    DeviceCategory,
    VisualScanStatusType,
    VisualScanFailedType,
    WifiScanStatusType
} from '@techsee/techsee-common/lib/constants/utils.constant';
import {SCAN_TYPE} from '@techsee/techsee-common/lib/constants/scan.constants';
import {
    Channel,
    IWifiAnalyzerResults,
    IWifiAnalyzerService
} from '../../../services/AngularServices/WifiAnalyzerService';
import {LOG_EVENTS} from '@techsee/techsee-common/lib/constants/event-logs.constants';
import {IEventLogsService} from '../../../services/EventsLogService';
import {ITsEnvironmentDetect} from '@techsee/techsee-common/lib/helpers/ts-environment-detect';
import {devicesOutlined, speedTest, tethering, wifi} from './icons';
import {WifiScanService, CG_WIFI_SCAN_TIMEOUT, CG_INFORMATIVE_COLOR, CG_ERROR_COLOR} from './WifiScanService';
import {ITranslate} from '../../../services/LocalizationService';
import isEmpty from 'lodash/isEmpty';
import {IChatApi} from '../../../services/ts-chat-api/ChatApi';
import {IRoom} from '../../../models/Room';
import {
    ScanResultBoxData,
    ConnectivityGuruThreshold,
    SpotLightLabels,
    ConnectivityHeatmapData,
    PointOfInterest,
    HeatmapImage
} from '../connectivity-guru.contracts';
import {RssiScaleBarLayer} from './components/layers/RssiScaleBarLayer';
import {MarkersLegendLayer} from './components/layers/MarkersLegendLayer';
import {MeshPointsLayer} from './components/layers/MeshPointsLayer';
import {AccessPointsInfoLayer, AccessPointData} from './components/layers/AccessPointsInfoLayer';
import {DEFAULT_RSSI_RANGE, RssiRangeConfig} from '../../../shared/image-layers/layer.types';
import {LayeredImageController} from '../../../shared/image-layers/LayeredImageController';

export enum WifiAnalyzerScanEvents {
    PUSH_WIFI_SCAN = 'PUSH_WIFI_SCAN',
    PUSH_VISUAL_SCAN = 'PUSH_VISUAL_SCAN',
    CHECK_MISSING_SCANS = 'CHECK_MISSING_SCANS'
}

interface ImageResource {
    url: string;
    _id?: string;
    type?: string;
    security?: {
        enabled: boolean;
    };
}

export interface IWifiAnalyzerScanController {
    readonly isVisible: boolean;
    readonly results: IWifiAnalyzerResults | null;

    readonly position: {x: number; y: number};
    readonly deviceCategory: DeviceCategory;
    readonly currentTab: number;
    readonly isScanning: boolean;
    readonly channelAnalysis?: Channel[];
    readonly connectivityHeatmapData?: ConnectivityHeatmapData;
    readonly connectivityGuruThreshold: ConnectivityGuruThreshold;
    readonly getSpotlightBox: () => ScanResultBoxData[];
    readonly showAdvanced: boolean;
    readonly isInsightsOpen: boolean;
    readonly showHeatmapImages: boolean;
    readonly showImagePreview: boolean;
    readonly selectedImage: {url: string; label?: string} | null;
    readonly isProcessingGradient: boolean;
    readonly enableRssiScaleBar: boolean;
    readonly enableOverlayLayers: boolean;
    readonly thumbnailLayeredImageController: LayeredImageController;
    readonly previewLayeredImageController: LayeredImageController;

    show(): void;
    hide(): void;
    setResults(results: IWifiAnalyzerResults): void;
    getFormattedTime(date: string): string;
    setCurrentTab(tab: number): void;
    getIconColor(tab: number): string;
    getCurrentColorByPoint(point: number): string;
    getDbmColor(point: number | undefined): string;
    startScan(): void;
    onClickAdvanced(): void;
    onClickBack(): void;
    setIsInsightsOpen(val: boolean): void;
    onImageClick(imageUrl: string, imageLabel?: string): void;
    onImagePreviewBack(): void;
    onImagePreviewEdit(imageUrl: string, imageLabel?: string): void;
    mergeImageWithScaleBar(imageElement: HTMLImageElement): Promise<string | null>;
    removeVisualScanStatusListener(): void;
    removeWifiScanStatusListener(): void;
    translate: ITranslate;
}

export class WifiAnalyzerScanController extends EventEmitter implements IWifiAnalyzerScanController {
    WifiAnalyzerScanService: WifiScanService;

    private visualScanStatusListener?: () => void;
    private visualScanFailedReasonListener?: () => void;

    private wifiScanStatusListener?: () => void;
    private chatApi?: IChatApi;
    private wifiAnalyzerService?: IWifiAnalyzerService;

    @observable isVisible: boolean = false;
    @observable connectivityGuruThreshold: ConnectivityGuruThreshold;
    @observable results: IWifiAnalyzerResults | null = null;
    @observable position: {x: number; y: number} = {x: 60, y: window.innerHeight - 0.7 * window.innerHeight};
    @observable deviceCategory: DeviceCategory = DeviceCategory.desktop;
    @observable currentTab: number = 0;
    @observable isMinimized: boolean = false;
    @observable isInsightsOpen: boolean = false;
    @observable isScanning: boolean = false;
    @observable connectivityHeatmapData?: ConnectivityHeatmapData;
    @observable channelAnalysis: Channel[] = [];
    @observable showAdvanced: boolean = false;
    @observable showHeatmapImages: boolean = true;
    @observable showImagePreview: boolean = false;
    @observable selectedImage: {url: string; label?: string} | null = null;
    @observable isProcessingGradient: boolean = false;
    @observable enableRssiScaleBar: boolean = true;
    @observable enableOverlayLayers: boolean = false;

    @observable public thumbnailLayeredImageController: LayeredImageController;
    @observable public previewLayeredImageController: LayeredImageController;

    constructor(
        connectivityGuruThreshold: ConnectivityGuruThreshold,
        isVisible: boolean,
        currentTab: number,
        public readonly translate: ITranslate,
        chatApi: IChatApi | undefined,
        private roomData: IRoom | undefined,
        wifiAnalyzerService: IWifiAnalyzerService | undefined,
        public readonly enableAgentWifiScanHistory: boolean,
        private readonly resetWifiScanStatus: () => void,
        private readonly setWifiScanDataForWifiAnalyzer: (data: IWifiAnalyzerResults) => void,
        private readonly selectClickedResource: (
            resource: ImageResource,
            room: string | {_id: string},
            updated: boolean
        ) => void | Promise<void>,
        private readonly displayToastMessage: (message: string, color: string, timeout: number) => void,
        private readonly clearCanvas: () => void,
        environmentService: ITsEnvironmentDetect,
        private eventsLogService: IEventLogsService,
        private getDateFormattedByAccLocal: (date: string, format: string) => string,
        displayTabletAsDesktop: boolean
    ) {
        super();

        this.connectivityGuruThreshold = connectivityGuruThreshold;
        this.isVisible = isVisible || false;
        this.currentTab = currentTab || 0;
        this.enableOverlayLayers = !!connectivityGuruThreshold?.layers?.enableFrequency;
        this.chatApi = chatApi;
        this.wifiAnalyzerService = wifiAnalyzerService;

        this.deviceCategory = environmentService.deviceCategory(displayTabletAsDesktop) || DeviceCategory.desktop;
        this.WifiAnalyzerScanService = new WifiScanService();
        this.setupScanStatusListener();

        this.thumbnailLayeredImageController = new LayeredImageController();
        this.previewLayeredImageController = new LayeredImageController();

        this.addOverlayLayers();
    }

    private addOverlayLayers(): void {
        if (this.enableRssiScaleBar) {
            const thumbnailLayer = new RssiScaleBarLayer(
                {
                    minRSSI: DEFAULT_RSSI_RANGE.minRSSI,
                    midRSSI: DEFAULT_RSSI_RANGE.midRSSI,
                    maxRSSI: DEFAULT_RSSI_RANGE.maxRSSI
                },
                {
                    id: 'rssi-scale-bar-thumbnail',
                    visible: true,
                    zIndex: this.RSSI_SCALE_BAR_Z_INDEX,
                    width: this.RSSI_SCALE_BAR_WIDTH_THUMBNAIL
                }
            );
            this.thumbnailLayeredImageController.addLayer(thumbnailLayer);

            const previewLayer = new RssiScaleBarLayer(
                {
                    minRSSI: DEFAULT_RSSI_RANGE.minRSSI,
                    midRSSI: DEFAULT_RSSI_RANGE.midRSSI,
                    maxRSSI: DEFAULT_RSSI_RANGE.maxRSSI
                },
                {
                    id: 'rssi-scale-bar-preview',
                    visible: true,
                    zIndex: this.RSSI_SCALE_BAR_Z_INDEX,
                    width: this.RSSI_SCALE_BAR_WIDTH_PREVIEW
                }
            );
            this.previewLayeredImageController.addLayer(previewLayer);
        }

        if (this.enableOverlayLayers) {
            const previewMeshPoints = new MeshPointsLayer({
                id: 'mesh-points-preview',
                visible: true,
                zIndex: this.MESH_POINTS_Z_INDEX
            });
            this.previewLayeredImageController.addLayer(previewMeshPoints);

            const previewMarkersLegend = new MarkersLegendLayer({
                id: 'markers-legend-preview',
                visible: true,
                zIndex: this.MARKERS_LEGEND_Z_INDEX
            });
            this.previewLayeredImageController.addLayer(previewMarkersLegend);

            const previewApInfo = new AccessPointsInfoLayer({
                id: 'access-points-info-preview',
                visible: true,
                zIndex: this.ACCESS_POINTS_INFO_Z_INDEX,
                headerText: this.translate('REACT.CONNECTIVITY_GURU.ACCESS_POINTS')
            });
            this.previewLayeredImageController.addLayer(previewApInfo);
        }
    }

    private updateAccessPointsLayers(accessPoints: AccessPointData[], selectedIndex: number = 0): void {
        const previewLayer = this.previewLayeredImageController.getLayer('access-points-info-preview');
        if (previewLayer instanceof AccessPointsInfoLayer) {
            previewLayer.setAccessPoints(accessPoints, selectedIndex);
        }
    }

    @action
    hide = () => {
        this.eventsLogService.info({
            logType: LOG_EVENTS.wifiScanResultModal,
            meta: {roomId: this.results?.roomId, status: 'modal closed'}
        });

        this.isVisible = false;
        this.results = null;
    };

    @action
    onClickAdvanced = () => {
        this.showAdvanced = true;
    };
    @action
    setIsInsightsOpen = (val: boolean) => {
        this.isInsightsOpen = val;
    };

    @action
    onClickBack = () => {
        this.showAdvanced = false;
    };

    @action
    onImageClick = (imageUrl: string, imageLabel?: string) => {
        this.selectedImage = {url: imageUrl, label: imageLabel};
        this.showImagePreview = true;
    };

    @action
    onImagePreviewBack = () => {
        this.showImagePreview = false;
        this.selectedImage = null;
    };

    private readonly RSSI_SCALE_BAR_WIDTH_THUMBNAIL = 350;
    private readonly RSSI_SCALE_BAR_WIDTH_PREVIEW = 400;
    private readonly RSSI_SCALE_BAR_Z_INDEX = 1;
    private readonly MARKERS_LEGEND_Z_INDEX = 2;
    private readonly ACCESS_POINTS_INFO_Z_INDEX = 3;
    private readonly MESH_POINTS_Z_INDEX = 0;

    private readonly MERGE_SCALE_BAR_WIDTH_RATIO = 0.9; // Scale bar takes 90% of canvas width
    private readonly MERGE_SCALE_BAR_PADDING = 20; // Bottom padding in pixels
    private readonly MERGE_SCALE_BAR_CENTER_OFFSET = 5; // Right offset to compensate for asymmetric labels

    private readonly BASE_BOX_PADDING_X = 8;
    private readonly BASE_BOX_PADDING_Y = 6;
    private readonly BASE_FONT_SIZE = 14;
    private readonly BASE_LABEL_MARGIN_TOP = 4;
    private readonly BASE_BAR_HEIGHT = 6;
    private readonly BASE_BORDER_RADIUS = 10;

    private readonly ALLOWED_RATIOS = [16 / 9, 4 / 3, 9 / 16, 3 / 4];

    private findClosestRatio = (imageRatio: number): number => {
        let closestRatio = this.ALLOWED_RATIOS[0];
        let minDiff = Math.abs(imageRatio - closestRatio);

        for (const ratio of this.ALLOWED_RATIOS) {
            const diff = Math.abs(imageRatio - ratio);
            if (diff < minDiff) {
                minDiff = diff;
                closestRatio = ratio;
            }
        }
        return closestRatio;
    };

    mergeImageWithScaleBar = async (imageElement: HTMLImageElement): Promise<string | null> => {
        if (!imageElement || !imageElement.complete) {
            return null;
        }

        const imageRatio = imageElement.naturalWidth / imageElement.naturalHeight;
        const targetRatio = this.findClosestRatio(imageRatio);

        return this.previewLayeredImageController.mergeImageWithLayers(imageElement, {
            targetAspectRatio: targetRatio,
            backgroundColor: '#000000',
            outputFormat: 'image/png',
            layerMergeOptions: {
                widthRatio: this.MERGE_SCALE_BAR_WIDTH_RATIO,
                baseWidth: this.RSSI_SCALE_BAR_WIDTH_PREVIEW,
                bottomPadding: this.MERGE_SCALE_BAR_PADDING,
                centerOffset: this.MERGE_SCALE_BAR_CENTER_OFFSET,
                baseStyles: {
                    boxPaddingX: this.BASE_BOX_PADDING_X,
                    boxPaddingY: this.BASE_BOX_PADDING_Y,
                    fontSize: this.BASE_FONT_SIZE,
                    labelMarginTop: this.BASE_LABEL_MARGIN_TOP,
                    barHeight: this.BASE_BAR_HEIGHT,
                    borderRadius: this.BASE_BORDER_RADIUS
                }
            }
        });
    };

    @action
    onImagePreviewEdit = (imageUrl: string, imageLabel?: string) => {
        if (!imageUrl || !this.roomData) {
            this.eventsLogService.error({
                logType: LOG_EVENTS.wifiScanResultModal,
                meta: {
                    message: 'Cannot edit image: imageUrl or roomData is missing',
                    hasImageUrl: !!imageUrl,
                    hasRoomData: !!this.roomData
                }
            });
            return;
        }

        const resource: ImageResource = {
            url: imageUrl,
            type: 'image'
        };

        this.eventsLogService.info({
            logType: LOG_EVENTS.wifiScanResultModal,
            meta: {
                roomId: this.roomData._id,
                action: 'edit_image_with_layers_merged',
                imageUrl,
                imageLabel
            }
        });

        this.clearCanvas();

        this.selectClickedResource(resource, this.roomData, false);

        this.showImagePreview = false;
        this.selectedImage = null;
    };

    @action
    setIsScanning = (isScanning: boolean) => {
        this.isScanning = isScanning;

        this.eventsLogService.info({
            logType: LOG_EVENTS.wifiScanResultInitiated,
            meta: {roomId: this.results?.roomId, isScanning}
        });
    };

    @action
    startScan = async () => {
        this.setIsScanning(true);
        if (this.results && this.wifiAnalyzerService) {
            this.wifiAnalyzerService.runScanner(
                this.results.roomId,
                this.results.id,
                (channels) => {
                    this.setChannelAnalysis(channels);
                    this.setIsScanning(false);
                },
                () => this.setIsScanning(false)
            );
        }
    };

    @action
    getFormattedTime = (date: string) => this.getDateFormattedByAccLocal(date, 'dd/MM/yyyy hh:mm');

    @action
    show = () => {
        this.isVisible = true;

        this.eventsLogService.info({
            logType: LOG_EVENTS.wifiScanResultModal,
            meta: {roomId: this.results?.roomId, status: 'modal opened'}
        });
    };
    @action
    getSpotlightBox = () => {
        const accessChannel =
            this.results?.channelAnalysis?.filter((val) => val.channel === this.results?.wifiConnectionInfo?.channel) ??
            [];

        const boxes: ScanResultBoxData[] = [
            {
                label: SpotLightLabels.download,
                value:
                    typeof this?.results?.networkSpeed?.downloadSpeedMbps === 'number'
                        ? this.results.networkSpeed.downloadSpeedMbps
                        : 0,
                unit: this.translate('CONNECTIVITY_GURU.WIFI_SCAN.MBPSUNIT'),
                icon: speedTest
            },
            {
                label: SpotLightLabels.upload,
                value:
                    typeof this?.results?.networkSpeed?.upSpeedMbps === 'number'
                        ? this.results.networkSpeed.upSpeedMbps
                        : 0,
                unit: this.translate('CONNECTIVITY_GURU.WIFI_SCAN.MBPSUNIT'),
                icon: speedTest
            },
            {
                label: SpotLightLabels.signal,
                value:
                    typeof this?.results?.wifiConnectionInfo?.rssi === 'number'
                        ? this.results.wifiConnectionInfo.rssi
                        : 0,
                wifiPercent:
                    typeof this.results?.wifiConnectionInfo?.signalStrengthPercentage === 'number'
                        ? this.results.wifiConnectionInfo.signalStrengthPercentage + '%'
                        : 'N/A',
                unit: this.translate('CONNECTIVITY_GURU.WIFI_SCAN.DBMUNIT'),
                icon: wifi
            },
            {
                label: SpotLightLabels.connectedDevices,
                value: !isEmpty(this?.results?.networkDiscoveryDevices)
                    ? this?.results?.networkDiscoveryDevices.length
                    : 0,
                unit: this.translate('CONNECTIVITY_GURU.WIFI_SCAN.DEVICESUNIT'),
                icon: devicesOutlined
            },
            {
                label: SpotLightLabels.channel,
                value: accessChannel.length && accessChannel[0].channel > 0 ? accessChannel[0].totalAP : 'N/A',
                wifiPercent:
                    this.results?.wifiConnectionInfo?.channel ??
                    (accessChannel.length && accessChannel[0].channel > 0 ? accessChannel[0].channel : '-'),
                unit: this.translate('CONNECTIVITY_GURU.WIFI_SCAN.ACCESS_POINTS'),
                icon: tethering
            }
        ];

        return boxes
            .map((box: ScanResultBoxData) => {
                box.levelOfSupport = this.getLevelOfSupportByLabels(box);

                box.onClick = action(() => {
                    switch (box.label) {
                        case SpotLightLabels.connectedDevices:
                            this.setCurrentTab(2); // Devices
                            break;
                        case SpotLightLabels.channel:
                            this.setCurrentTab(1); // Channels
                            break;
                        case SpotLightLabels.signal:
                        case SpotLightLabels.upload:
                        case SpotLightLabels.download:
                            this.setCurrentTab(0); // MyNetwork
                            break;
                    }

                    this.onClickAdvanced();
                });

                return box;
            })
            .filter((box: ScanResultBoxData) => box.levelOfSupport !== 0);
    };
    @action
    getLevelOfSupportByLabels = (box: ScanResultBoxData): number => {
        if (box.value == null) return 1; // NA

        switch (box.label) {
            case SpotLightLabels.download:
                return this.WifiAnalyzerScanService.evalThreshold(
                    SpotLightLabels.download,
                    box.value as number,
                    this.connectivityGuruThreshold.downloadSpeed,
                    true
                );

            case SpotLightLabels.upload:
                return this.WifiAnalyzerScanService.evalThreshold(
                    SpotLightLabels.upload,
                    box.value as number,
                    this.connectivityGuruThreshold.uploadSpeed,
                    true
                );

            case SpotLightLabels.signal:
                return this.WifiAnalyzerScanService.evalThreshold(
                    SpotLightLabels.signal,
                    box.value as number,
                    this.connectivityGuruThreshold.signalStrength,
                    true
                );

            case SpotLightLabels.connectedDevices:
                return this.WifiAnalyzerScanService.evalThreshold(
                    SpotLightLabels.connectedDevices,
                    box.value as number,
                    this.connectivityGuruThreshold.connectedDevices,
                    false // more devices = worse
                );

            case SpotLightLabels.channel:
                return this.WifiAnalyzerScanService.evalThreshold(
                    SpotLightLabels.channel,
                    box.value as number,
                    this.connectivityGuruThreshold.channelCongestion,
                    false // higher congestion = worse
                );

            default:
                return 1;
        }
    };

    @action
    setResults = (results: IWifiAnalyzerResults) => {
        this.eventsLogService.info({
            logType: LOG_EVENTS.wifiScanResultModal,
            meta: {roomId: results.roomId, results, status: 'results set'}
        });
        this.connectivityHeatmapData = results?.connectivityHeatmapData;

        // Validate and set channel analysis
        this.channelAnalysis = results.channelAnalysis || [];

        // Set the results
        this.results = results;

        // Update RSSI layers if enabled
        if (this.enableRssiScaleBar) {
            const rssiConfig = results.rssiConfig || DEFAULT_RSSI_RANGE;

            // Remove existing layers before adding new ones
            if (this.thumbnailLayeredImageController.getLayer('rssi-scale-bar-thumbnail')) {
                this.thumbnailLayeredImageController.removeLayer('rssi-scale-bar-thumbnail');
            }
            if (this.previewLayeredImageController.getLayer('rssi-scale-bar-preview')) {
                this.previewLayeredImageController.removeLayer('rssi-scale-bar-preview');
            }

            // Thumbnail layer (350px)
            const thumbnailLayer = new RssiScaleBarLayer(
                {
                    minRSSI: rssiConfig.minRSSI,
                    midRSSI: rssiConfig.midRSSI,
                    maxRSSI: rssiConfig.maxRSSI
                },
                {
                    id: 'rssi-scale-bar-thumbnail',
                    visible: true,
                    zIndex: this.RSSI_SCALE_BAR_Z_INDEX,
                    width: this.RSSI_SCALE_BAR_WIDTH_THUMBNAIL
                }
            );
            this.thumbnailLayeredImageController.addLayer(thumbnailLayer);

            // Preview layer (400px)
            const previewLayer = new RssiScaleBarLayer(
                {
                    minRSSI: rssiConfig.minRSSI,
                    midRSSI: rssiConfig.midRSSI,
                    maxRSSI: rssiConfig.maxRSSI
                },
                {
                    id: 'rssi-scale-bar-preview',
                    visible: true,
                    zIndex: this.RSSI_SCALE_BAR_Z_INDEX,
                    width: this.RSSI_SCALE_BAR_WIDTH_PREVIEW
                }
            );
            this.previewLayeredImageController.addLayer(previewLayer);
        }

        this.updateOverlayLayersFromResults(results);

        this.isVisible = true;
        this.showImagePreview = false;
    };

    private updateOverlayLayersFromResults(results: IWifiAnalyzerResults): void {
        if (!this.enableOverlayLayers) return;

        const heatmapData = results?.connectivityHeatmapData;
        if (!heatmapData) return;

        this.updateMeshPointsFromResults(heatmapData);
        this.updateMarkersLegendFromResults(heatmapData.pointOfInterest, heatmapData.imageUrls);
        this.updateAccessPointsFromResults(heatmapData.imageUrls);
    }

    private updateMeshPointsFromResults(heatmapData: ConnectivityHeatmapData): void {
        const previewLayer = this.previewLayeredImageController.getLayer('mesh-points-preview');
        if (previewLayer instanceof MeshPointsLayer) {
            previewLayer.setMeshPoints(
                heatmapData.pointOfInterest || [],
                heatmapData.imageWidth,
                heatmapData.imageHeight
            );
        }
    }

    private updateMarkersLegendFromResults(pointsOfInterest?: PointOfInterest[], imageUrls?: HeatmapImage[]): void {
        const previewLayer = this.previewLayeredImageController.getLayer('markers-legend-preview');
        if (previewLayer instanceof MarkersLegendLayer) {
            if (!pointsOfInterest?.length) {
                previewLayer.setItems([]);
            } else {
                previewLayer.setItemsFromPointsOfInterest(pointsOfInterest, imageUrls || []);
            }
        }
    }

    private updateAccessPointsFromResults(imageUrls?: HeatmapImage[]): void {
        const apMap = new Map<string, AccessPointData>();

        if (imageUrls?.length) {
            imageUrls.forEach((img) => {
                if (img.type !== 'bssid_heatmap') return;
                if (!img.ssid || !img.bssid) return;

                const existing = apMap.get(img.ssid);
                if (existing) {
                    const hasBand = existing.bands.some((b) => b.bssid === img.bssid);
                    if (!hasBand) {
                        existing.bands.push({frequency: img.frequency, bssid: img.bssid});
                    }
                } else {
                    apMap.set(img.ssid, {
                        ssid: img.ssid,
                        bands: [{frequency: img.frequency, bssid: img.bssid}]
                    });
                }
            });
        }

        const accessPoints = Array.from(apMap.values());
        this.updateAccessPointsLayers(accessPoints);
    }

    @action
    setChannelAnalysis = (channelAnalysis: Channel[]) => {
        this.eventsLogService.info({
            logType: LOG_EVENTS.wifiScanResultModal,
            meta: {roomId: this.results?.roomId, channelAnalysis, status: 'channel analysis set'}
        });

        this.channelAnalysis = channelAnalysis;
    };

    @action
    setCurrentTab = (tab: number) => {
        this.currentTab = tab;
    };

    getIconColor = (level: number): string => {
        return this.WifiAnalyzerScanService.getIconColor(level);
    };

    getCurrentColorByPoint = (level: number): string => {
        return this.WifiAnalyzerScanService.getCurrentColorByPoint(level);
    };
    getDbmColor = (dbm: number | undefined): string => {
        return this.WifiAnalyzerScanService.getDbmColor(dbm, this.results?.rssiConfig);
    };

    /**
     * Gets existing scan IDs - now returns empty array, listeners will handle this via events
     */
    private getExistingScanIds(): string[] {
        // Event-based architecture: Return empty array, let listeners handle sync
        return [];
    }

    public processMissingScans(missingScans: IWifiAnalyzerResults[]) {
        try {
            const sortedScans = missingScans.sort((a, b) => {
                const dateA = new Date(a.createdOn).getTime();
                const dateB = new Date(b.createdOn).getTime();

                return dateA - dateB;
            });

            for (const scan of sortedScans) {
                const scanId = scan.id || scan._id;

                if (scan.scanDetails?.scanType === SCAN_TYPE.ar) {
                    // Emit event for visual scan
                    this.emit(WifiAnalyzerScanEvents.PUSH_VISUAL_SCAN, scan);

                    this.eventsLogService.info({
                        logType: LOG_EVENTS.wifiScanHistoryResults,
                        meta: {
                            scanId,
                            scanType: 'visual/ar',
                            message: 'Emitted event to push scan to visualScanResults'
                        }
                    });
                } else {
                    // Emit event for wifi scan
                    if (!this.enableAgentWifiScanHistory) {
                        this.setWifiScanDataForWifiAnalyzer(scan);
                    } else {
                        this.emit(WifiAnalyzerScanEvents.PUSH_WIFI_SCAN, scan);
                    }

                    this.eventsLogService.info({
                        logType: LOG_EVENTS.wifiScanHistoryResults,
                        meta: {
                            scanId,
                            scanType: scan.scanDetails?.scanType || 'wifi',
                            message: 'Emitted event to push scan to wifiScanResults'
                        }
                    });
                }
            }
        } catch (error) {
            this.eventsLogService.error({
                logType: LOG_EVENTS.wifiScanHistoryResultsError.type,
                meta: {
                    error,
                    message: 'Failed to push missing scans to dashboard state'
                }
            });
        }
    }

    /**
     * Checks for missing scan IDs and syncs them via event emission
     */
    async checkForMissingScanIds() {
        try {
            if (!this.roomData) {
                return;
            }

            const roomId = this.roomData._id;
            const customerId = this.roomData.customerId;

            if (!customerId) {
                return;
            }

            this.eventsLogService.info({
                logType: LOG_EVENTS.wifiScanHistoryResultsReq,
                meta: {
                    roomId,
                    customerId,
                    message: 'Emitting event to check for missing scans'
                }
            });

            // Emit event for listeners to handle missing scans check
            this.emit(WifiAnalyzerScanEvents.CHECK_MISSING_SCANS, {roomId, customerId});
        } catch (error) {
            this.eventsLogService.error({
                logType: LOG_EVENTS.wifiScanHistoryResultsError.type,
                meta: {
                    error,
                    roomId: this.roomData?._id,
                    customerId: this.roomData?.customerId,
                    message: 'Failed to emit check missing scans event'
                }
            });
        }
    }

    /**
     * Handles visual scan status changes
     */
    private handleVisualScanStatusChange = () => {
        if (!this.chatApi) {
            return;
        }

        if (
            this.chatApi.client.visualScanStatus === VisualScanStatusType.Idle ||
            this.chatApi.client.visualScanStatus === VisualScanStatusType.NotInitialized
        ) {
            switch (this.chatApi.client.visualScanLastFailedReason) {
                case VisualScanFailedType.none:
                    // Scan completed successfully - check for missing scan IDs
                    this.checkForMissingScanIds();
                    break;

                case VisualScanFailedType.cameraDeny:
                    if (this.displayToastMessage) {
                        this.displayToastMessage(
                            'CONNECTIVITY_GURU.VISUAL_SCAN_FAILED.CUSTOMER_DENIED_REQUEST_CAMERA',
                            CG_ERROR_COLOR,
                            CG_WIFI_SCAN_TIMEOUT
                        );
                    }
                    break;

                case VisualScanFailedType.visualScanDeny:
                    if (this.displayToastMessage) {
                        this.displayToastMessage(
                            'CONNECTIVITY_GURU.VISUAL_SCAN_FAILED.CUSTOMER_DENIED_REQUEST_WIFI',
                            CG_ERROR_COLOR,
                            CG_WIFI_SCAN_TIMEOUT
                        );
                    }
                    break;

                case VisualScanFailedType.abort:
                    if (this.displayToastMessage) {
                        this.displayToastMessage(
                            'CONNECTIVITY_GURU.VISUAL_SCAN_FAILED.ABORT',
                            CG_INFORMATIVE_COLOR,
                            CG_WIFI_SCAN_TIMEOUT
                        );
                    }
                    break;
            }
        } else if (this.chatApi.client.visualScanStatus === VisualScanStatusType.InProgress) {
            if (this.displayToastMessage) {
                this.displayToastMessage(
                    'CONNECTIVITY_GURU.VISUAL_SCAN.WIFI_SCAN_START',
                    CG_INFORMATIVE_COLOR,
                    CG_WIFI_SCAN_TIMEOUT
                );
            }
        }
    };
    private handleWifiScanStatusChange = () => {
        if (!this.chatApi) {
            return;
        }

        if (this.chatApi.client.wifiScanStatus === WifiScanStatusType.Failed) {
            if (this.displayToastMessage) {
                this.displayToastMessage(
                    'CONNECTIVITY_GURU.WIFI_SCAN.WIFI_SCAN_IN_FAILED',
                    CG_ERROR_COLOR,
                    CG_WIFI_SCAN_TIMEOUT
                );
            }
        }
        if (
            this.chatApi.client.wifiScanStatus === WifiScanStatusType.Failed ||
            this.chatApi.client.wifiScanStatus === WifiScanStatusType.Succeeded
        ) {
            this.resetWifiScanStatus();
            this.checkForMissingScanIds().then();
        }

        if (this.chatApi.client.wifiScanStatus !== WifiScanStatusType.InProgress) {
            this.chatApi.setStatus('wifiScanStatus', WifiScanStatusType.Idle, true);
        }
    };

    wifiScanResultsListener = () => {};

    /**
     * Sets up the visual scan status listener
     */
    private setupScanStatusListener = () => {
        if (!this.chatApi) {
            return;
        }

        this.visualScanStatusListener = () => {
            this.handleVisualScanStatusChange();
        };

        this.visualScanFailedReasonListener = () => {
            this.handleVisualScanStatusChange();
        };

        this.wifiScanStatusListener = () => {
            this.handleWifiScanStatusChange();
        };

        this.chatApi.on('clientVisualScanStatus', this.visualScanStatusListener);
        this.chatApi.on('clientVisualScanLastFailedReason', this.visualScanFailedReasonListener);
        this.chatApi.on('clientWifiScanStatus', this.wifiScanStatusListener);
    };

    /**
     * Removes the visual scan status listener for cleanup
     */
    public removeVisualScanStatusListener = () => {
        if (this.chatApi && this.visualScanStatusListener) {
            this.chatApi.off('clientVisualScanStatus', this.visualScanStatusListener);
            this.visualScanStatusListener = undefined;
        }
        if (this.chatApi && this.visualScanFailedReasonListener) {
            this.chatApi.off('clientVisualScanLastFailedReason', this.visualScanFailedReasonListener);
            this.visualScanFailedReasonListener = undefined;
        }
    };

    public removeWifiScanStatusListener = () => {
        if (this.chatApi && this.wifiScanStatusListener) {
            this.chatApi.off('clientWifiScanStatus', this.wifiScanStatusListener);
            this.wifiScanStatusListener = undefined;
        }
    };
}
